import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Combine, Crop, Grid3x3, Loader2, Maximize2, ScanSearch, Sparkles, Spline, Square, Undo2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import {
  croppedCalibration,
  cropOf,
  DEFAULT_CALIBRATION,
  FULL_IMAGE_CROP,
  frameShapesToDrawing,
  isShapedWall,
  pinShapesToFrame,
  quadPosition,
  sameCrop,
  shapeLedStarts,
  uncroppedCalibration,
} from '@/components/climbs/board-grid/geometry';
import { ScanPlaceholderPicker } from '../scans/ScanPlaceholderPicker';
import { BoardLedLinesEditor, type LedLinesMode } from './BoardLedLinesEditor';
import { BoardLedLineList } from './BoardLedLineList';
import { BoardLedSettings, type LedSettings } from './BoardLedSettings';
import {
  holdShapes,
  holdsTouch,
  ledByPlace,
  ledStarts as ledLineStarts,
  moveLineEnd,
  sameJoint,
  splitLineAt,
  threadLines,
  type PhotoPoint,
} from './led-lines';
import { ScanWallTools } from '../scans/ScanWallTools';
import { useScanWallTools, type ScanWallApi } from '../scans/useScanWallTools';
import { boardHoldScansService } from '../../../services/board-hold-scans';
import type { ClimbScan } from '../../../types/climb-scan';
import BoardCalibrationEditor, { type CalibrationTool } from '../BoardCalibrationEditor';
import BoardImageUpload from '../BoardImageUpload';
import { BoardShapeList } from './BoardShapeList';
import { devicesService } from '../../../services/devices';
import type {
  Board,
  BoardLedLine,
  BoardCalibration,
  BoardDisabledCells,
  BoardExtraHoldMap,
  BoardImageCrop,
  BoardShapes,
  CalibrationPoint,
} from '../../../types/device';

interface BoardGridPanelProps {
  gymSlug: string;
  board: Board;
  /**
   * The photo as it was uploaded, not the window the board is drawn through:
   * the crop is edited here, so widening one has to be possible, and a corner
   * an earlier crop cut off has to be somewhere you can still grab.
   */
  imageUrl: string;
  /**
   * The dashboard's own drag handle, drawn between the two columns this panel
   * fills. Handed in rather than built here so the wall/controls split is the
   * same one the wall/list split uses, and stays where the reader last put it.
   */
  separator: ReactNode;
  /**
   * The saved board, so the page redraws its problems against the new wall —
   * and whether the problems were taken off it, which the list has to be pulled
   * back from the server to show.
   */
  onSaved: (board: Board, climbsCleared: boolean, newPhoto: boolean) => void;
  onCancel: () => void;
}

/**
 * How often the scan of the board's photo is read back while it runs.
 */
const HOLD_SCAN_POLL_MS = 5000;

/**
 * The tool in hand: the holds the scan found (the wall itself), the LED lines
 * drawn on them, or one of the editor's legacy tools.
 */
type PanelTool = CalibrationTool | 'holds';

/**
 * Describing the wall: where its holds are, which of them exist, and what the
 * picture of it shows.
 *
 * **A gym's own wall is described by the scan of its photo**
 * (docs/SPRAY_WALL_SCAN_PLAN.md): "AI magic" finds the holds, and the card of
 * the photo carries what the scans page does to a wall's holds — the
 * confidence slider, excluding, adding by hand, an area looked at again — each
 * saved on the spot. The lines are for a wall with a device, to say which LED
 * lights which hold. The grid, the corners, the empty cells and the off-grid
 * holds are no longer offered; their code stays for the boards described with
 * them, until a follow-up removes it.
 *
 * Two kinds of wall are described here, and the tools are the same for both.
 * A **ruled** wall is a rectangle of rows and columns, aligned by dragging the
 * four corners onto the corner holds. A **drawn** wall — a spray wall, whose
 * holes follow the panels rather than a grid — is a run of straight lines with a
 * number of holds strung along each, drawn directly onto the photo. Once the
 * lines are there, the corners, the crop and the crossed-off cells go on meaning
 * exactly what they meant: the lines live in the quad's own coordinates, so the
 * four corners move the whole drawing at once, the way they move a grid.
 *
 * The board form on `/boards` used to offer the same four handles, but a dialog
 * gives the photo a few hundred pixels, and a wall is 25 holds across: the
 * handles could not be placed to within a hold of where they belonged, so the
 * grid they described was wrong wherever it was dragged. Here the photo takes a
 * resizable half of the window, and the problems already on the wall are drawn
 * beside it to check the alignment against.
 *
 * Everything on this screen is measured against the *uploaded* photo, including
 * the four corners, and only turns into what the board stores on the way out
 * (see croppedCalibration). That is what lets the crop be dragged about without
 * the grid coming off the wall: the wall does not move on the original picture,
 * whatever window is cut out of it.
 *
 * Takes over both columns for as long as it is open, the way the problem form
 * does: the photo on the left at the width it already had, and everything done
 * to it on the right where the list of problems was. A strip of buttons under
 * the photo pushed the wall up the screen and put the tool being used furthest
 * from the hand using it — and the list has nothing to say about a grid.
 */
export function BoardGridPanel({
  gymSlug,
  board,
  imageUrl,
  separator,
  onSaved,
  onCancel,
}: BoardGridPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const savedCrop = cropOf(board.image_crop);
  // The wall as this panel opened it, which for a drawn wall is not quite the
  // wall as it was stored: the quad is nailed to the picture on the way in (see
  // openDrawing). Nothing moves by it, so it is also what "has this been
  // redrawn" is asked against.
  const [opened] = useState(() => openDrawing(board, savedCrop));
  const [crop, setCrop] = useState(savedCrop);
  const [calibration, setCalibration] = useState<BoardCalibration>(opened.calibration);
  const [extraHolds, setExtraHolds] = useState<BoardExtraHoldMap>(opened.extraHolds);
  const [disabledCells, setDisabledCells] = useState<BoardDisabledCells>(board.disabled_cells ?? []);
  const [shapes, setShapes] = useState<BoardShapes>(opened.shapes);
  const [selectedShape, setSelectedShape] = useState<number | null>(null);
  // A photo picked in here, uploaded to storage already: the signed id goes
  // with the save, the preview is what the editor draws on in the meantime.
  const [image, setImage] = useState<{ signedId: string; previewUrl: string } | null>(null);
  // **Two kinds of wall** (BoardType#layout): a grid of rows and columns
  // (Moonboard, a gym's Grid panel), whose holds are its cells placed by four
  // corners, and a spray wall, whose holds are the ones its photo's scan
  // found. A grid opens on its corners, a spray wall on its holds.
  const gridWall = board.board_type.layout === 'grid';
  const [tool, setTool] = useState<PanelTool>(gridWall ? 'corners' : 'holds');
  const [saving, setSaving] = useState(false);
  // Whether the "this will leave the problems pointing at nothing" question is
  // on screen, and nothing more: the answer goes straight into the save.
  const [confirming, setConfirming] = useState(false);

  // A grid's rows and columns, as typed on "Edit grid": what the editor draws
  // as soon as they are numbers, the saved ones until then.
  const savedRows = board.rows ?? board.board_type.rows ?? null;
  const savedColumns = board.columns ?? board.board_type.columns ?? null;
  const [gridSize, setGridSize] = useState({
    rows: savedRows?.toString() ?? '',
    columns: savedColumns?.toString() ?? '',
  });
  const rows = wholeCount(gridSize.rows) ?? savedRows;
  const columns = wholeCount(gridSize.columns) ?? savedColumns;
  const resized = gridWall && (rows !== savedRows || columns !== savedColumns);
  // How the LEDs look and run, on a wall with a device to light them.
  const [leds, setLeds] = useState<LedSettings>(() => ({
    brightness: board.led_brightness?.toString() ?? '',
    highlightColor: board.resolved_circuit_highlight_color,
    origin: board.led_origin ?? '',
    direction: board.led_direction ?? '',
  }));
  // A wall with a strip on it has no LED between two t-nuts, so it could never
  // point at a screw-on. Until a gym can say which LED they spliced in for one,
  // these belong to the boards nobody has wired — the same rule the model
  // enforces, so the button is not offered where the save would be refused.
  //
  // A wall drawn as lines is the exception: there the holes the strip reaches
  // are the ones on the lines, so an extra hold is by then exactly the hold that
  // lights nothing — a volume, a screw-on. Read off the lines as they stand
  // rather than as they were saved, since both travel in the same request.
  const drawn = isShapedWall(shapes);
  const canPlaceExtras = !board.device_id || drawn;
  const cropChanged = !sameCrop(crop, savedCrop);
  const ledStarts = useMemo(() => shapeLedStarts(shapes), [shapes]);
  // Whether the holds the problems point at are different holds now — asked of
  // the photo rather than of the stored numbers, and see addressing for why the
  // two are not the same question. Compared rather than tracked by a flag: a
  // line drawn and deleted again has changed nothing.
  const shapesChanged = useMemo(
    () =>
      !sameHoles(
        addressing(opened.calibration, opened.shapes),
        addressing(calibration, shapes)
      ),
    [calibration, shapes, opened]
  );
  // Redrawing the legacy lines moves the holds out from under the problems
  // already on the wall. A new photo does too, but that is asked when it is
  // picked, and answered by archiving them (see archiving).
  const redrawn = shapesChanged;
  const climbs = board.climbs_count ?? 0;
  // **A new photo archives every problem on the wall**: they name holds of the
  // old one. Asked before the file picker opens (confirmingPhoto), and the
  // answer goes with the save.
  // A grid's holds are its cells, which a new photo does not move: only the
  // corners have to be dragged onto it again.
  const archiving = !!image && climbs > 0 && !gridWall;
  const [confirmingPhoto, setConfirmingPhoto] = useState(false);
  const pickPhoto = useRef<(() => void) | null>(null);

  /**
   * **The scan of the board's photo**, read on opening and every few seconds
   * while it runs. Null while the photo has none.
   */
  const [holdScan, setHoldScan] = useState<ClimbScan | null>(null);
  const [holdScanLoaded, setHoldScanLoaded] = useState(false);
  const [holdScanBusy, setHoldScanBusy] = useState(false);
  const reloadHoldScan = useCallback(async () => {
    try {
      const scan = await boardHoldScansService.get(gymSlug, board.id);
      setHoldScan(scan);
      return scan;
    } catch {
      return null;
    } finally {
      setHoldScanLoaded(true);
    }
  }, [gymSlug, board.id]);

  useEffect(() => {
    // A grid has no scan to read: its holds are its cells.
    if (gridWall) return;

    void reloadHoldScan();
  }, [reloadHoldScan, gridWall]);

  const scanRunning = holdScan?.status === 'pending' || holdScan?.status === 'processing';
  useEffect(() => {
    if (!scanRunning) return;

    const timer = setInterval(() => void reloadHoldScan(), HOLD_SCAN_POLL_MS);
    return () => clearInterval(timer);
  }, [scanRunning, reloadHoldScan]);

  // The scan's photo is the upload, the same picture the editor draws on; a
  // new photo picked but not saved yet has no scan to show.
  const scanPhoto = holdScan?.status === 'ready' && !image ? holdScan.image_url : null;
  const [photoSize, setPhotoSize] = useState<{ url: string; width: number; height: number } | null>(null);
  useEffect(() => {
    if (!scanPhoto) return;

    const picture = new Image();
    picture.onload = () =>
      setPhotoSize({ url: scanPhoto, width: picture.naturalWidth, height: picture.naturalHeight });
    picture.src = scanPhoto;
  }, [scanPhoto]);
  const naturalSize = photoSize && photoSize.url === scanPhoto ? photoSize : null;

  const wallApi: ScanWallApi = useMemo(
    () => ({
      update: (_scanId, attrs) => boardHoldScansService.update(gymSlug, board.id, attrs),
      addHold: (_scanId, outline) => boardHoldScansService.addHold(gymSlug, board.id, outline),
      scanArea: (_scanId, area) => boardHoldScansService.scanArea(gymSlug, board.id, area),
      reload: () => reloadHoldScan(),
    }),
    [gymSlug, board.id, reloadHoldScan]
  );
  const wallTools = useScanWallTools({
    scan: holdScan,
    setScan: setHoldScan,
    api: wallApi,
    naturalSize,
    resetKey: tool,
  });

  /**
   * **The LED lines of a scanned wall** (docs/SPRAY_WALL_SCAN_PLAN.md): drawn
   * on the scan's shown holds, each click snapped to one, every hold a line
   * crosses taken in the order it meets them — except the holds marked as
   * having no LED. Threaded on every change, so what is drawn is what is sent.
   * A problem names the hold and not the LED, so none of this asks anything
   * about the problems on the wall.
   */
  const scannedWall = holdScan?.status === 'ready' && !image;
  const [ledLines, setLedLines] = useState<BoardLedLine[]>(board.led_lines ?? []);
  const [unlitHolds, setUnlitHolds] = useState<number[]>(board.unlit_holds ?? []);
  const [selectedLedLine, setSelectedLedLine] = useState<number | null>(null);
  const [linesMode, setLinesMode] = useState<LedLinesMode>('draw');
  const unlitSet = useMemo(() => new Set(unlitHolds), [unlitHolds]);
  const lineHolds = useMemo(
    () => (holdScan ? holdShapes(holdScan.detected_holds, wallTools.shownAt) : []),
    [holdScan, wallTools.shownAt]
  );
  const ledHolds = useMemo(() => threadLines(ledLines, lineHolds, unlitSet), [ledLines, lineHolds, unlitSet]);
  const ledsByPlace = useMemo(() => ledByPlace(ledLines, ledHolds), [ledLines, ledHolds]);
  const ledLineStartsList = useMemo(() => ledLineStarts(ledLines, ledHolds), [ledLines, ledHolds]);

  /**
   * **The lines are drawn as one run**, the way the strip is stapled: the
   * first click puts a start down, and every click after it closes a line and
   * opens the next from where it stopped. "New line" breaks the run, for a
   * strip in another piece — the next click is a start again.
   */
  const [pendingStart, setPendingStart] = useState<PhotoPoint | null>(null);
  const [runBroken, setRunBroken] = useState(false);

  /** A click on the photo, already pulled onto the hold it was near. */
  const placeLedPoint = (point: PhotoPoint) => {
    const lastEnd = ledLines.at(-1)?.points.at(-1);
    const start = pendingStart ?? (runBroken ? undefined : lastEnd);
    if (!start) {
      setPendingStart(point);
      return;
    }
    // A click on the end it would start from draws nothing.
    if (sameJoint(start, point)) return;

    setLedLines([...ledLines, { points: [start, point], led_start: null }]);
    setSelectedLedLine(ledLines.length);
    setPendingStart(null);
    setRunBroken(false);
  };

  /** An end dragged: the line next to it follows when the two are one joint. */
  const moveLedEnd = (line: number, point: number, to: PhotoPoint) =>
    setLedLines((current) => moveLineEnd(current, line, point, to));

  /**
   * "Add a joint": the line through the hold pressed is cut there, and the new
   * joint is ready to drag — back to drawing, where the handles are.
   */
  const splitLedLine = (place: number) => {
    const split = splitLineAt(ledLines, ledHolds, lineHolds, place);
    if (!split) return;

    setLedLines(split.lines);
    setSelectedLedLine(split.line);
    setLinesMode('draw');
  };

  /** A strip in another piece: the next click starts a line of its own. */
  const startLedLine = () => {
    setRunBroken(true);
    setPendingStart(null);
    setSelectedLedLine(null);
    setLinesMode('draw');
  };

  /** The last line is almost always the mistake to undo. */
  const undoLedLine = () => {
    if (pendingStart) {
      setPendingStart(null);
      return;
    }
    setLedLines(ledLines.slice(0, -1));
    setSelectedLedLine(null);
  };

  const toggleUnlit = (place: number) =>
    setUnlitHolds((current) =>
      current.includes(place) ? current.filter((other) => other !== place) : [...current, place].sort((a, b) => a - b)
    );

  /**
   * **Two touching holds, offered as one**: when the two holds lit on the
   * photo touch, they are most likely one hold the detector cut in two. Joined
   * on the server (ClimbScans::MergeHolds): the joined hold added, the two
   * excluded — nothing deleted, so a problem on either keeps it.
   */
  const [chosenA, chosenB] = wallTools.chosenAt;
  const mergeable = useMemo(() => {
    if (!holdScan || chosenA === undefined || chosenB === undefined || wallTools.chosenAt.length !== 2) return null;

    const [one, other] = holdShapes(holdScan.detected_holds, [chosenA, chosenB]);
    return one && other && holdsTouch(one, other) ? ([chosenA, chosenB] as [number, number]) : null;
  }, [holdScan, chosenA, chosenB, wallTools.chosenAt.length]);
  const [merging, setMerging] = useState(false);

  const mergeHolds = async () => {
    if (!mergeable) return;
    setMerging(true);
    try {
      setHoldScan(await boardHoldScansService.mergeHolds(gymSlug, board.id, mergeable));
      toast({ title: t('gymAdmin.boardDashboard.grid.merge.done') });
    } catch (error) {
      toast({
        title: t('gymAdmin.boardDashboard.grid.merge.error'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setMerging(false);
    }
  };

  /** "AI magic": the saved photo, sent to be read. */
  const startHoldScan = async () => {
    setHoldScanBusy(true);
    try {
      setHoldScan(await boardHoldScansService.start(gymSlug, board.id));
      setTool('holds');
    } catch (error) {
      toast({
        title: t('gymAdmin.boardDashboard.grid.scan.startError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setHoldScanBusy(false);
    }
  };

  /** Stop: a run that will not come back, or that nobody wants any more. */
  const stopHoldScan = async () => {
    setHoldScanBusy(true);
    try {
      setHoldScan(await boardHoldScansService.stop(gymSlug, board.id));
    } catch {
      void reloadHoldScan();
    } finally {
      setHoldScanBusy(false);
    }
  };

  /**
   * The drawing, and the quad it is read in, after one of them has moved. A
   * framing that stood down (null) leaves the quad alone and takes the drawing
   * as it came.
   */
  const apply = (next: BoardShapes, moved: ReturnType<typeof pinShapesToFrame>) => {
    if (!moved) return setShapes(next);

    setShapes(moved.shapes);
    setCalibration(moved.calibration);
    setExtraHolds(moved.extraHolds);
  };

  /** The drawing with the quad nailed back to the picture — see pickTool. */
  const pin = (next: BoardShapes, frame: BoardImageCrop) =>
    apply(next, pinShapesToFrame(calibration, next, extraHolds, frame));

  /**
   * A change to the drawing, with the quad kept on the picture.
   *
   * Reachable only while the lines are the tool in hand — that is the one tool
   * that draws them — which is also where the quad belongs on the picture: it
   * is what keeps the reach a point may be placed within meaning one picture
   * rather than one sliver of a drawing. The pin moves no hold on the photo, so
   * it costs nothing to do on every stroke, and the screw-ons come along
   * because they are stored in the same coordinates.
   */
  const applyShapes = (next: BoardShapes) => pin(next, crop);

  /**
   * A change to the window, with the quad following it on a drawn wall.
   *
   * The drawing itself does not move: everything on this screen is measured
   * against the uploaded photo, and cutting a window out of a photo moves
   * nothing on it. What moves is which fractions of the window the ends come
   * out as, and leaving the quad on the old window would walk those towards the
   * edge of what may be stored one crop at a time.
   */
  const applyCrop = (next: BoardImageCrop) => {
    setCrop(next);
    pin(shapes, next);
  };

  /**
   * Picking up a tool, which on a drawn wall also decides where the quad sits.
   *
   * The four corners are in two places depending on what is being done with
   * them, and the move between them keeps every hold on its pixel — see
   * movedShapes. Taking them in hand builds the quad **around the lines**, so
   * the handles are on the wall and dragging one carries the drawing as one
   * body; letting go of them nails it back to the **picture**, so the reach a
   * point may be placed within is a picture rather than a sliver of a drawing
   * and the rest of the wall can still be drawn on.
   */
  const pickTool = (next: PanelTool) => {
    setTool(next);
    if (!drawn || next === tool) return;

    if (next === 'corners') {
      return apply(shapes, frameShapesToDrawing(calibration, shapes, extraHolds));
    }
    if (tool === 'corners') pin(shapes, crop);
  };

  const save = async (deleteClimbs: boolean) => {
    setConfirming(false);
    setSaving(true);
    try {
      const saved = await devicesService.updateBoardGrid(gymSlug, board.id, {
        // Back into the coordinates of the picture the board is drawn on —
        // the window, not the upload the handles were placed against.
        calibration: croppedCalibration(calibration, crop),
        // Only ever sent by a board allowed to have them, so a wired wall's
        // stored holds are left alone rather than cleared by a re-alignment.
        extraHolds: canPlaceExtras ? extraHolds : undefined,
        disabledCells,
        shapes,
        // Left out when the window has not moved: sending it re-cuts the
        // picture, and a plain re-alignment has no reason to.
        crop: cropChanged ? crop : undefined,
        imageSignedId: image?.signedId,
        deleteClimbs,
        archiveClimbs: archiving,
        // A scanned wall's LED lines, the ones with somewhere to go: a line of
        // one point is a line not drawn yet. A new photo clears them on the
        // server, so they are not sent with one.
        // Only once they are numbers that changed: leaving them out keeps the
        // grid the wall has.
        grid:
          resized && board.board_type.resizable_grid && rows && columns ? { rows, columns } : undefined,
        leds: board.device_id
          ? {
              // Empty leaves the strip on whatever brightness it has, which is
              // a null rather than a 0 — a 0 would turn it off.
              led_brightness: leds.brightness === '' ? null : Number(leds.brightness),
              circuit_highlight_color: leds.highlightColor,
              ...(gridWall ? { led_origin: leds.origin || null, led_direction: leds.direction || null } : {}),
            }
          : undefined,
        ...(scannedWall
          ? (() => {
              const drawnLines = ledLines.filter((line) => line.points.length >= 2);
              return {
                ledLines: drawnLines,
                ledHolds: threadLines(drawnLines, lineHolds, unlitSet),
                unlitHolds,
              };
            })()
          : {}),
      });

      // **A save is a save, not a way out**: the panel stays open on the wall
      // as it is, so work in progress is kept rather than closed. Only a new
      // photo starts it over — its scan has just been started, and the wall
      // is a new one.
      onSaved(saved, deleteClimbs || archiving, !!image);
      toast({ title: t('gymAdmin.boardDashboard.grid.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.boardDashboard.grid.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Saving asks first, when there is something to lose by it.
   *
   * A problem names its holds by where they sit on the wall — which line, how
   * far along it — so new lines, or a new photo to draw them on, leave every
   * problem on the wall pointing at holds that have moved. Nothing in the data
   * says which hole a hold used to be, so the choice is the gym's: clear the
   * wall, or keep the problems and fix them by hand.
   */
  const requestSave = () =>
    (redrawn || resized) && climbs > 0 && !image ? setConfirming(true) : save(false);

  // A grid is described by its corners (and, where the type allows, its rows
  // and columns), after the crop. A spray wall by its scan's holds, its LED
  // strips (only with a device — there is nothing to light otherwise) and the
  // crop; corners, empty cells and off-grid holds are no longer offered there,
  // their code staying for the walls already described with them.
  const cropTool = {
    key: 'crop' as const,
    icon: Crop,
    label: t('gymAdmin.boardDashboard.grid.tools.crop'),
    hint: t('gymAdmin.boardDashboard.grid.tools.cropHint'),
  };
  const tools: Array<{ key: PanelTool; icon: typeof Crop; label: string; hint: string }> = gridWall
    ? [
        cropTool,
        {
          key: 'corners',
          icon: Grid3x3,
          label: t('gymAdmin.boardDashboard.grid.tools.grid'),
          hint: t('gymAdmin.boardDashboard.grid.tools.gridHint'),
        },
      ]
    : [
        {
          key: 'holds',
          icon: ScanSearch,
          label: t('gymAdmin.boardDashboard.grid.tools.holds'),
          hint: t('gymAdmin.boardDashboard.grid.tools.holdsHint'),
        },
        ...(board.device_id
          ? [
              {
                key: 'shapes' as const,
                icon: Spline,
                label: t('gymAdmin.boardDashboard.grid.tools.shapes'),
                hint: t('gymAdmin.boardDashboard.grid.tools.shapesHint'),
              },
            ]
          : []),
        cropTool,
      ];

  const active = tools.find((entry) => entry.key === tool) ?? tools[0];

  return (
    <>
      {/* Left: the wall, scrolling on its own and nothing else on it — the
          photo is what every gesture lands on, so it gets the whole column. */}
      <div className="lg:min-h-0 lg:overflow-y-auto">
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            {tool === 'holds' ? (
              scanPhoto && naturalSize ? (
                <ScanPlaceholderPicker
                  key={wallTools.picker.key}
                  imageUrl={scanPhoto}
                  naturalWidth={naturalSize.width}
                  naturalHeight={naturalSize.height}
                  placeholders={wallTools.picker.placeholders}
                  maybe={wallTools.picker.maybe}
                  onChosenChange={wallTools.picker.onChosenChange}
                  wand={wallTools.picker.wand}
                  areaScan={wallTools.picker.areaScan}
                  testId="board-scan-wall"
                />
              ) : (
                // No holds to show yet: the photo as it is, the card on the
                // right saying what is going on with its scan.
                <img
                  src={image?.previewUrl ?? imageUrl}
                  alt=""
                  className="block w-full"
                  data-testid="board-wall-photo"
                />
              )
            ) : tool === 'shapes' && scannedWall && scanPhoto ? (
              <BoardLedLinesEditor
                imageUrl={scanPhoto}
                holds={lineHolds}
                unlit={unlitSet}
                lines={ledLines}
                leds={ledsByPlace}
                ledHolds={ledHolds}
                selectedLine={selectedLedLine}
                pendingStart={pendingStart}
                mode={linesMode}
                onPlace={placeLedPoint}
                onMoveEnd={moveLedEnd}
                onSelectLine={setSelectedLedLine}
                onSplit={splitLedLine}
                onToggleUnlit={toggleUnlit}
              />
            ) : (
            <BoardCalibrationEditor
              imageUrl={image?.previewUrl ?? imageUrl}
              value={calibration}
              onChange={setCalibration}
              rows={rows}
              columns={columns}
              holds={board.holds}
              extraHolds={extraHolds}
              onExtraHoldsChange={canPlaceExtras ? setExtraHolds : undefined}
              disabledCells={disabledCells}
              onDisabledCellsChange={setDisabledCells}
              shapes={shapes}
              onShapesChange={applyShapes}
              selectedShape={selectedShape}
              onSelectShape={setSelectedShape}
              crop={crop}
              onCropChange={applyCrop}
              tool={tool}
            />
            )}
          </CardContent>
        </Card>
      </div>

      {separator}

      {/* Right: what is being done to the wall, in the column the list of
          problems was in. From `lg` the card takes that column's height and
          scrolls inside itself, so the way out and the way through stay a bar
          at the bottom of the screen — the same shape the problem form has. */}
      <div className="lg:min-h-0">
        <Card className="lg:flex lg:h-full lg:flex-col lg:overflow-hidden">
          <CardContent className="p-0 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
            <div className="space-y-3 p-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
              <p className="text-sm text-muted-foreground">
                {t(gridWall ? 'gymAdmin.boardDashboard.grid.gridWallHint' : 'gymAdmin.boardDashboard.grid.wallHint')}
              </p>

              {/* One job at a time: they all want the same drag on the same
                  photo, and a press meant for one landing in another is how a
                  hold ends up somewhere nobody put it. Stacked rather than in a
                  row — a column this wide fits one button, and the tool in hand
                  is easier to read down a list than along a wrapped line. */}
              <div className="flex flex-col gap-1" role="group">
                {tools.map(({ key, icon: Icon, label }) => (
                  <Button
                    key={key}
                    type="button"
                    variant={tool === key ? 'secondary' : 'outline'}
                    size="sm"
                    aria-pressed={tool === key}
                    className="justify-start"
                    onClick={() => pickTool(key)}
                  >
                    <Icon className="mr-2 size-4" aria-hidden />
                    {label}
                  </Button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                {tool === 'shapes' && scannedWall
                  ? t('gymAdmin.boardDashboard.grid.tools.ledLinesHint')
                  : active.hint}
              </p>

              {tool === 'shapes' && scannedWall && (
                <div className="space-y-2" data-testid="board-led-lines-controls">
                  {/* Three things a press on the photo can mean: the next point
                      of a line, a joint where a line crosses this hold, or
                      "this hold has no LED". */}
                  <div className="flex flex-col gap-1" role="group">
                    {(['draw', 'split', 'unlit'] as const).map((mode) => (
                      <Button
                        key={mode}
                        type="button"
                        size="sm"
                        className="justify-start"
                        variant={linesMode === mode ? 'secondary' : 'outline'}
                        aria-pressed={linesMode === mode}
                        onClick={() => setLinesMode(mode)}
                      >
                        {t(`gymAdmin.boardDashboard.grid.ledLines.mode.${mode}`)}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(`gymAdmin.boardDashboard.grid.ledLines.modeHint.${linesMode}`, { count: unlitHolds.length })}
                  </p>

                  <BoardLedSettings value={leds} onChange={setLeds} />

                  <BoardLedLineList
                    lines={ledLines}
                    ledHolds={ledHolds}
                    ledStarts={ledLineStartsList}
                    selected={selectedLedLine}
                    onSelect={setSelectedLedLine}
                    onChange={setLedLines}
                  />

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-muted-foreground">
                      {t('gymAdmin.boardDashboard.grid.ledLines.total', {
                        count: ledHolds.reduce((sum, places) => sum + places.length, 0),
                      })}
                    </p>
                    <div className="flex gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={startLedLine}>
                        <Spline className="mr-2 size-4" aria-hidden />
                        {t('gymAdmin.boardDashboard.grid.ledLines.newLine')}
                      </Button>
                      {(ledLines.length > 0 || pendingStart) && (
                        <Button type="button" variant="ghost" size="sm" onClick={undoLedLine}>
                          <Undo2 className="mr-2 size-4" aria-hidden />
                          {t('gymAdmin.boardDashboard.grid.ledLines.undo')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {tool === 'shapes' && !scannedWall && (
                <div className="space-y-2">
                  <BoardShapeList
                    shapes={shapes}
                    ledStarts={ledStarts}
                    selected={selectedShape}
                    onSelect={setSelectedShape}
                    onChange={applyShapes}
                  />

                  {shapes.length > 0 && (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground">
                        {t('gymAdmin.boardDashboard.grid.shapes.total', {
                          count: shapes.reduce((sum, shape) => sum + shape.holds, 0),
                        })}
                      </p>
                      {/* Drawing a wall is a run of clicks, so the mistake to
                          undo is almost always the last one. */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          applyShapes(shapes.slice(0, -1));
                          setSelectedShape(null);
                        }}
                      >
                        <Undo2 className="mr-2 size-4" aria-hidden />
                        {t('gymAdmin.boardDashboard.grid.shapes.undo')}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {gridWall && tool === 'corners' && (
                <div className="space-y-3" data-testid="board-grid-controls">
                  {board.board_type.resizable_grid && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label htmlFor="board-grid-rows" className="text-xs">
                          {t('gymAdmin.boardDashboard.grid.size.rows')}
                        </Label>
                        <Input
                          id="board-grid-rows"
                          type="number"
                          min={1}
                          className="h-9"
                          value={gridSize.rows}
                          onChange={(event) => setGridSize((prev) => ({ ...prev, rows: event.target.value }))}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="board-grid-columns" className="text-xs">
                          {t('gymAdmin.boardDashboard.grid.size.columns')}
                        </Label>
                        <Input
                          id="board-grid-columns"
                          type="number"
                          min={1}
                          className="h-9"
                          value={gridSize.columns}
                          onChange={(event) => setGridSize((prev) => ({ ...prev, columns: event.target.value }))}
                        />
                      </div>
                    </div>
                  )}
                  {board.board_type.global && resized && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {t('gymAdmin.boardDashboard.grid.size.libraryWarning')}
                    </p>
                  )}
                  {board.device_id && <BoardLedSettings value={leds} onChange={setLeds} wiring />}
                </div>
              )}

              {tool === 'crop' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  onClick={() => applyCrop(FULL_IMAGE_CROP)}
                >
                  <Maximize2 className="mr-2 size-4" aria-hidden />
                  {t('gymAdmin.boardDashboard.grid.resetCrop')}
                </Button>
              )}

              {/* The photo itself. Here because this is the screen the wall is
                  described on, and a wall that has been repainted or rebuilt is
                  photographed again before any of the rest of this means
                  anything. A new one is its own original: whatever window was
                  cut out of the last picture describes nothing in this one, so
                  the crop goes back to the whole frame. */}
              <div className="space-y-3 rounded-md border p-3" data-testid="board-photo-card">
                <div className="space-y-1">
                  <p className="text-xs font-medium">
                    {t('gymAdmin.boardDashboard.grid.image.title')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      gridWall
                        ? 'gymAdmin.boardDashboard.grid.image.gridHint'
                        : image && board.hold_scan_offered
                          ? 'gymAdmin.boardDashboard.grid.image.scanOnSave'
                          : 'gymAdmin.boardDashboard.grid.image.hint'
                    )}
                  </p>
                </div>
                <BoardImageUpload
                  currentImageUrl={null}
                  label={t('gymAdmin.boardDashboard.grid.image.change')}
                  onBeforePick={
                    climbs > 0 && !gridWall
                      ? (pick) => {
                          pickPhoto.current = pick;
                          setConfirmingPhoto(true);
                        }
                      : undefined
                  }
                  onUploaded={(signedId, previewUrl) => {
                    setImage({ signedId, previewUrl });
                    applyCrop(FULL_IMAGE_CROP);
                  }}
                />

                {!image && holdScanLoaded && !gridWall && (
                  <BoardHoldScanStatus
                    scan={holdScan}
                    offered={!!board.hold_scan_offered}
                    busy={holdScanBusy}
                    onStart={() => void startHoldScan()}
                    onStop={() => void stopHoldScan()}
                    shownCount={wallTools.placeholders.length}
                  />
                )}

                {/* The scans page's tools for the wall's holds, acting on the
                    photo on the left — so only while it shows them. */}
                {!image && tool === 'holds' && mergeable && (
                  <div
                    className="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2"
                    data-testid="board-merge-holds"
                  >
                    <p className="text-xs">{t('gymAdmin.boardDashboard.grid.merge.offer')}</p>
                    <Button type="button" size="sm" className="w-full" disabled={merging} onClick={() => void mergeHolds()}>
                      {merging ? (
                        <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                      ) : (
                        <Combine className="mr-2 size-4" aria-hidden />
                      )}
                      {t('gymAdmin.boardDashboard.grid.merge.confirm')}
                    </Button>
                  </div>
                )}

                {!image && wallTools.tools && (
                  tool === 'holds' ? (
                    <ScanWallTools {...wallTools.tools} />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => pickTool('holds')}
                    >
                      <ScanSearch className="mr-2 size-4" aria-hidden />
                      {t('gymAdmin.boardDashboard.grid.scan.showHolds')}
                    </Button>
                  )
                )}
              </div>
            </div>

            {/* Outside the scroll area, so they are on screen wherever the
                column has been scrolled to. */}
            <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-end gap-2 rounded-b-lg border-t bg-card p-3">
              <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="button" size="sm" onClick={requestSave} disabled={saving}>
                {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmingPhoto} onOpenChange={(open) => !open && setConfirmingPhoto(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymAdmin.boardDashboard.grid.archive.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymAdmin.boardDashboard.grid.archive.description', { count: climbs })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="board-archive-confirm"
              onClick={() => {
                setConfirmingPhoto(false);
                pickPhoto.current?.();
              }}
            >
              {t('gymAdmin.boardDashboard.grid.archive.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('gymAdmin.boardDashboard.grid.redraw.title', { count: climbs })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymAdmin.boardDashboard.grid.redraw.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            {/* Keeping them is the quieter answer, so it is the plain button and
                not the one the dialog pushes you towards. */}
            <Button type="button" variant="outline" onClick={() => save(false)}>
              {t('gymAdmin.boardDashboard.grid.redraw.keep')}
            </Button>
            <AlertDialogAction
              data-testid="board-redraw-clear"
              onClick={() => save(true)}
            >
              {t('gymAdmin.boardDashboard.grid.redraw.clear')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Where the scan of the board's photo is: "AI magic" while it has none (or the
 * last one failed or was stopped), a spinner and Stop while it runs, and how
 * many holds the wall has once it is read.
 */
function BoardHoldScanStatus({
  scan,
  offered,
  busy,
  onStart,
  onStop,
  shownCount,
}: {
  scan: ClimbScan | null;
  offered: boolean;
  busy: boolean;
  onStart: () => void;
  onStop: () => void;
  shownCount: number;
}) {
  const { t } = useTranslation();
  const running = scan?.status === 'pending' || scan?.status === 'processing';

  if (running) {
    const progress = scan?.progress;
    return (
      <div className="flex items-center gap-2" data-testid="board-hold-scan-running">
        <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        <p className="flex-1 text-xs text-muted-foreground">
          {progress?.stage === 'tiles'
            ? t('gymAdmin.boardDashboard.grid.scan.progress', { done: progress.done, total: progress.total })
            : t('gymAdmin.boardDashboard.grid.scan.running')}
        </p>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onStop}>
          <Square className="mr-2 size-3" aria-hidden />
          {t('gymAdmin.boardDashboard.grid.scan.stop')}
        </Button>
      </div>
    );
  }

  if (scan?.status === 'ready') {
    return (
      <p className="text-xs text-muted-foreground" data-testid="board-hold-scan-ready">
        {t('gymAdmin.boardDashboard.grid.scan.holds', { count: shownCount })}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {scan?.status === 'failed' && (
        <p className={scan.stopped ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}>
          {scan.stopped
            ? t('gymAdmin.boardDashboard.grid.scan.stopped')
            : t('gymAdmin.boardDashboard.grid.scan.failed')}
        </p>
      )}
      {offered ? (
        <Button type="button" size="sm" className="w-full" disabled={busy} onClick={onStart}>
          {busy ? (
            <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
          ) : (
            <Sparkles className="mr-2 size-4" aria-hidden />
          )}
          {t('gymAdmin.boardDashboard.grid.scan.start')}
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">{t('gymAdmin.boardDashboard.grid.scan.notOffered')}</p>
      )}
    </div>
  );
}

/**
 * The wall as this panel takes it in hand: the corners read back onto the
 * uploaded photo, and — on a wall drawn as lines — the quad nailed to the
 * window the board is drawn through.
 *
 * The pin happens here rather than being waited for, because a wall stored with
 * a quad shrunk onto its own drawing cannot be drawn on: the reach a point is
 * held to is measured in quads, so on such a board a click a hand's width from
 * the lines was being dropped in silence. It moves nothing on the photo (see
 * pinShapesToFrame), which is what lets it be done to a board nobody asked to
 * have changed.
 */
function openDrawing(board: Board, crop: BoardImageCrop) {
  const calibration =
    uncroppedCalibration(board.calibration ?? DEFAULT_CALIBRATION, crop) ?? DEFAULT_CALIBRATION;
  const shapes = board.shapes ?? [];
  const extraHolds = board.extra_holds ?? {};

  return pinShapesToFrame(calibration, shapes, extraHolds, crop) ?? { calibration, shapes, extraHolds };
}

/**
 * What a problem's holds are addressed by: where each line runs **on the
 * uploaded photo**, and how many holds are strung along it.
 *
 * A hold is the cell `(which line, how far along it)`, so what breaks a problem
 * is a line coming to run between different holes — one added, deleted or
 * reordered, a count that no longer reaches the same hole, an end dragged onto
 * another, the whole drawing carried off by a corner.
 *
 * Read on the photo rather than in the quad's coordinates, because those are not
 * the same question. The quad of a drawn wall moves between two framings as the
 * tools are picked up (see pickTool), which renumbers every end without moving
 * any of them — and a gym who merely looked at the corners must not be asked to
 * choose between its problems and that.
 *
 * `taper` is left out for the same reason read the other way: it moves a line's
 * holds on the photo without changing which hole each one is. The fourth hold of
 * a line is the fourth hole along it before and after; it is simply drawn where
 * it really is instead of where even spacing guessed. Correcting a photo that
 * was never quite straight is not redrawing the wall. `led_start` is out too: it
 * says which lamp lights a hold, never which hole a problem points at.
 */
function addressing(calibration: BoardCalibration, shapes: BoardShapes) {
  return shapes.map((shape) => ({
    from: quadPosition(calibration, shape.from.u, shape.from.v),
    to: quadPosition(calibration, shape.to.u, shape.to.v),
    holds: Math.max(0, Math.trunc(shape.holds)),
  }));
}

/**
 * Whether two drawings run their lines between the same holes of the same photo.
 * Give or take the float of a framing and back, which is exact on paper and a
 * few parts in a quadrillion in a double.
 */
function sameHoles(
  before: ReturnType<typeof addressing>,
  after: ReturnType<typeof addressing>
): boolean {
  const near = (p: CalibrationPoint, q: CalibrationPoint) =>
    Math.abs(p.x - q.x) < 1e-9 && Math.abs(p.y - q.y) < 1e-9;

  return (
    before.length === after.length &&
    before.every(
      (line, at) =>
        line.holds === after[at].holds &&
        near(line.from, after[at].from) &&
        near(line.to, after[at].to)
    )
  );
}

/** A count typed into a field, or null while it is not one yet. */
function wholeCount(value: string): number | null {
  const count = Number(value);
  return value.trim() !== '' && Number.isInteger(count) && count >= 1 ? count : null;
}
